import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { GoogleMap } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import { cellToLatLng } from 'h3-js';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';
import { DailyProfileChart, HexAnalyticsModal, SEASON_COLORS } from './hexForecastCharts';
import { formatEnergyKwh, formatPowerKw, powerScaleFromKw } from '../../utils/formatEnergyUnits';
import { buildRelativeEnergyScale, getHexColor } from '../../utils/hexEnergyScale';
import {
    NATIVE_HEX_RES,
    aggregateHexes,
    resolutionForZoom,
    safeResolution,
    selectionForDisplay,
} from '../../utils/hexAggregation';

const SIDEBAR_WIDTH = 420;
const CA_MAP_CENTER = { lat: 36.7783, lng: -119.4179 };
const CA_MAP_ZOOM = 6;
const HEX_ZOOM_ON_SELECT = 10;
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAP_OPTIONS = {
    mapTypeId: 'roadmap',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControlOptions: { position: 3 },
    styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
};

const CA_HD_CASE_NAME = 'California Baseline 2027';
const CA_HD_YEAR = 2027;
const TRANSIT_YEARS = [2027, 2028, 2029, 2030];
const LOAD_FILTERS = {
    combined: 'combined',
    hd: 'hd',
    transit: 'transit',
};

function num(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function transitEnergyFromRow(row) {
    if (!row) return 0;
    if (row.transit_annual_energy_kwh != null && row.transit_annual_energy_kwh !== '') {
        return num(row.transit_annual_energy_kwh);
    }
    if (row.hd_annual_energy_kwh != null) {
        return Math.max(0, num(row.annual_energy_kwh) - num(row.hd_annual_energy_kwh));
    }
    return num(row.annual_energy_kwh);
}

function transitPeakFromRow(row) {
    if (!row) return 0;
    if (row.transit_peak_power_kw != null && row.transit_peak_power_kw !== '') {
        return num(row.transit_peak_power_kw);
    }
    if (row.hd_annual_energy_kwh != null || row.hd_peak_power_kw != null) {
        return 0;
    }
    return num(row.peak_power_kw);
}

/** Union of HD and transit hexes. A cell is kept if either source has load. */
function mergeLoadHexes(hdRows, transitRows) {
    const byKey = new Map();
    const ensure = (rawIndex) => {
        const display = String(rawIndex || '').trim();
        const key = display.toLowerCase();
        if (!key) return null;
        if (!byKey.has(key)) {
            byKey.set(key, {
                h3_index: display,
                hd_annual_energy_kwh: 0,
                transit_annual_energy_kwh: 0,
                hd_peak_power_kw: 0,
                transit_peak_power_kw: 0,
                annual_energy_kwh: 0,
                peak_power_kw: 0,
            });
        }
        return byKey.get(key);
    };

    (hdRows || []).forEach((row) => {
        const rec = ensure(row?.h3_index);
        if (!rec) return;
        rec.hd_annual_energy_kwh = num(row.annual_energy_kwh);
        rec.hd_peak_power_kw = num(row.peak_power_kw);
    });
    (transitRows || []).forEach((row) => {
        const rec = ensure(row?.h3_index);
        if (!rec) return;
        rec.transit_annual_energy_kwh = transitEnergyFromRow(row);
        rec.transit_peak_power_kw = transitPeakFromRow(row);
        rec.week_kwh = row.week_kwh;
        rec.n_vehicles = row.n_vehicles;
        rec.n_events = row.n_events;
        if (rec.lat == null) rec.lat = row.lat;
        if (rec.lng == null) rec.lng = row.lng;
    });

    return Array.from(byKey.values())
        .map((rec) => ({
            ...rec,
            annual_energy_kwh: rec.hd_annual_energy_kwh + rec.transit_annual_energy_kwh,
            peak_power_kw: rec.hd_peak_power_kw + rec.transit_peak_power_kw,
        }))
        .filter((rec) => rec.hd_annual_energy_kwh > 0 || rec.transit_annual_energy_kwh > 0);
}

function displayEnergyKwh(hex, loadFilter) {
    if (loadFilter === LOAD_FILTERS.hd) return num(hex.hd_annual_energy_kwh);
    if (loadFilter === LOAD_FILTERS.transit) return num(hex.transit_annual_energy_kwh);
    return num(hex.hd_annual_energy_kwh) + num(hex.transit_annual_energy_kwh);
}

function hexMatchesFilter(hex, loadFilter) {
    if (loadFilter === LOAD_FILTERS.hd) return num(hex.hd_annual_energy_kwh) > 0;
    if (loadFilter === LOAD_FILTERS.transit) return num(hex.transit_annual_energy_kwh) > 0;
    return num(hex.hd_annual_energy_kwh) > 0 || num(hex.transit_annual_energy_kwh) > 0;
}

function formatCount(value) {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return Math.round(n).toLocaleString();
}

function formatKwh(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return '—';
    return formatEnergyKwh(n);
}

function formatKw(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return formatPowerKw(n);
}

function formatDailyEnergy(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return formatEnergyKwh(n);
}

function analysisForFilter(profile, loadFilter) {
    if (!profile) return null;
    if (loadFilter === LOAD_FILTERS.hd) return profile.hd_analysis || null;
    if (loadFilter === LOAD_FILTERS.transit) return profile.transit_analysis || null;
    return profile.combined_analysis || null;
}

function analysisSubtitle(loadFilter) {
    if (loadFilter === LOAD_FILTERS.hd) return 'Hex Profile Analysis · Heavy Duty';
    if (loadFilter === LOAD_FILTERS.transit) return 'Hex Profile Analysis · Transit Bus';
    return 'Hex Profile Analysis · Combined HD + Transit';
}

function HourSeriesChart({ values = [], color = '#7c3aed', title, xLabels }) {
    const safe = Array.isArray(values) ? values.map((v) => Number(v || 0)) : [];
    if (!safe.length || !safe.some((v) => v > 0)) {
        return <p className="text-xs text-gray-400 italic py-2">No data for this period.</p>;
    }
    const W = SIDEBAR_WIDTH - 48;
    const H = 140;
    const pL = 44;
    const pR = 8;
    const pT = 6;
    const pB = 26;
    const iW = W - pL - pR;
    const iH = H - pT - pB;
    const max = Math.max(...safe, 1e-9);
    const yScale = powerScaleFromKw(safe);
    const xf = (i) => pL + (iW * i) / Math.max(safe.length - 1, 1);
    const yf = (v) => pT + iH - (v / max) * iH;
    const pts = safe.map((v, i) => `${xf(i)},${yf(v)}`).join(' ');
    const area = `${pL},${pT + iH} ${pts} ${W - pR},${pT + iH}`;
    const tickEvery = safe.length > 24 ? 24 : 6;
    return (
        <div>
            {title && (
                <p className="text-xs font-medium text-gray-500 mb-1">
                    {String(title).replace(/\(kW\)/i, `(${yScale.unit})`)}
                </p>
            )}
            <svg width={W} height={H}>
                {[0, max / 2, max].map((tv, i) => {
                    const y = yf(tv);
                    return (
                        <g key={i}>
                            <line x1={pL} y1={y} x2={W - pR} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={pL - 3} y={y + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
                                {yScale.formatTick(tv)}
                            </text>
                        </g>
                    );
                })}
                {safe.map((_, i) => (i % tickEvery === 0 ? i : null)).filter((i) => i != null).map((h) => (
                    <g key={h}>
                        <line x1={xf(h)} y1={pT + iH} x2={xf(h)} y2={pT + iH + 3} stroke="#9ca3af" strokeWidth="1" />
                        <text x={xf(h)} y={pT + iH + 13} textAnchor="middle" fontSize="8" fill="#9ca3af">
                            {xLabels?.[h] || (safe.length > 24 ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][h / 24] : `${h}:00`)}
                        </text>
                    </g>
                ))}
                <polygon points={area} fill={color} fillOpacity="0.12" />
                <polyline fill="none" stroke={color} strokeWidth="2" points={pts} />
            </svg>
        </div>
    );
}

function HexSearchBar({ hexData, onSelect }) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const inputRef = useRef(null);
    const hexIndex = useMemo(() => {
        const m = {};
        (hexData || []).forEach((h) => { m[h.h3_index] = h; });
        return m;
    }, [hexData]);

    const commit = useCallback((h3Index) => {
        const match = hexIndex[h3Index];
        if (match) {
            setQuery(match.h3_index);
            setSuggestions([]);
            onSelect(match);
        }
    }, [hexIndex, onSelect]);

    return (
        <div className="relative">
            <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                    const val = e.target.value;
                    setQuery(val);
                    const needle = val.trim().toLowerCase();
                    setSuggestions(
                        needle
                            ? Object.values(hexIndex).filter((h) => h.h3_index.toLowerCase().includes(needle)).slice(0, 6)
                            : []
                    );
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commit(suggestions[0]?.h3_index || query.trim());
                    }
                }}
                placeholder="H3 index"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 focus:border-violet-400 focus:outline-none"
            />
            {suggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
                    {suggestions.map((h) => (
                        <button
                            key={h.h3_index}
                            type="button"
                            onClick={() => commit(h.h3_index)}
                            className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-violet-50"
                        >
                            {h.h3_index}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function CaHexOverlay({ map, hexData, onHexClick, selectedH3Index, loadFilter, displayRes = NATIVE_HEX_RES, energyScale }) {
    const overlayRef = useRef(null);
    const [overlayReady, setOverlayReady] = useState(false);
    const layers = useMemo(() => {
        if (!hexData || !hexData.length) return [];
        return [
            new H3HexagonLayer({
                id: `ca-gridplan-hex-${loadFilter}-${displayRes}`,
                data: hexData,
                pickable: true,
                filled: true,
                stroked: true,
                extruded: false,
                highPrecision: true,
                getHexagon: (d) => String(d.h3_index),
                getFillColor: (d) => getHexColor(
                    d.display_energy_kwh ?? displayEnergyKwh(d, loadFilter),
                    energyScale,
                ),
                getLineColor: (d) => (d.h3_index === selectedH3Index ? [124, 58, 237, 255] : [255, 255, 255, 45]),
                lineWidthMinPixels: 1,
                getLineWidth: (d) => (d.h3_index === selectedH3Index ? 6 : 1),
                parameters: { depthTest: false },
                updateTriggers: {
                    getFillColor: [loadFilter, energyScale?.key, energyScale?.maxKwh, displayRes],
                    getLineColor: [selectedH3Index],
                    getLineWidth: [selectedH3Index],
                },
                onClick: (info) => { if (info?.object) onHexClick(info.object); },
            }),
        ];
    }, [hexData, selectedH3Index, onHexClick, loadFilter, energyScale, displayRes]);

    useEffect(() => {
        if (!map || overlayRef.current) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            if (cancelled) return;
            try {
                const overlay = new GoogleMapsOverlay({ interleaved: false, pickingRadius: 8 });
                overlay.setMap(map);
                overlayRef.current = overlay;
                setOverlayReady(true);
            } catch (_) {}
        }, 100);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            setOverlayReady(false);
            if (overlayRef.current) {
                try { overlayRef.current.setMap(null); } catch (_) {}
                overlayRef.current = null;
            }
        };
    }, [map]);

    useEffect(() => {
        if (!overlayReady || !overlayRef.current) return;
        overlayRef.current.setProps({ layers });
    }, [layers, overlayReady]);

    return null;
}

function TransitReportTab({ report = {}, year }) {
    const agencies = Array.isArray(report.agencies) ? report.agencies : [];
    const stockByYear = report.cec_stock_by_year || {};
    const iepr = report.cec_stock_iepr || {};
    const ieprYears = Object.keys(iepr).map(Number).sort((a, b) => a - b);
    return (
        <div className="min-h-0 flex-1 overflow-auto bg-white p-6">
            <div className="mx-auto max-w-6xl space-y-6">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Development report</p>
                    <h2 className="text-lg font-bold text-gray-900">Transit electrification inputs · {year}</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        {report.share_formula || 'CEC stock / statewide GTFS routes'} applied uniformly to each agency.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {[
                        ['CEC bus stock', formatCount(report.cec_stock)],
                        ['GTFS bus routes', formatCount(report.total_routes)],
                        ['Electrify share', report.electrify_share != null ? `${(Number(report.electrify_share) * 100).toFixed(2)}%` : '—'],
                        ['Agencies', formatCount(report.n_agencies)],
                        ['Simulated vehicle-weeks', formatCount(report.n_blocks_simulated)],
                        ['Native GTFS blocks', formatCount(report.n_blocks_native)],
                        ['FIFO backfilled blocks', formatCount(report.n_blocks_fifo_backfilled)],
                        ['Electrified vehicle-weeks', formatCount(report.n_blocks_electrified)],
                    ].map(([label, value]) => (
                        <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
                            <p className="text-base font-semibold tabular-nums text-gray-900">{value}</p>
                        </div>
                    ))}
                </div>
                {report.share_capped && (
                    <p className="text-xs text-amber-700">
                        CEC stock exceeds statewide route count, so the applied share is capped at 100%.
                    </p>
                )}
                <div>
                    <p className="mb-2 text-sm font-semibold text-gray-900">CEC transit-bus electricity stock</p>
                    <div className="overflow-x-auto rounded-md border border-gray-200">
                        <table className="min-w-full text-sm tabular-nums">
                            <thead className="bg-gray-50 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                <tr>
                                    <th className="px-3 py-2">Year</th>
                                    <th className="px-3 py-2">Stock</th>
                                    <th className="px-3 py-2">Used for forecast</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ieprYears.map((y) => (
                                    <tr key={y} className={`border-t border-gray-100 ${y === year ? 'bg-violet-50' : ''}`}>
                                        <td className="px-3 py-1.5">{y}</td>
                                        <td className="px-3 py-1.5">{formatCount(iepr[y])}</td>
                                        <td className="px-3 py-1.5 text-gray-500">
                                            {stockByYear[y] != null ? '2027–2030 input' : 'reference only'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-400">{report.source}</p>
                </div>
                <div>
                    <p className="mb-2 text-sm font-semibold text-gray-900">Per-agency electrification</p>
                    <div className="overflow-x-auto rounded-md border border-gray-200">
                        <table className="min-w-full text-xs tabular-nums">
                            <thead className="bg-gray-50 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                <tr>
                                    <th className="px-2 py-2">Agency</th>
                                    <th className="px-2 py-2">Feed</th>
                                    <th className="px-2 py-2 text-right">Routes</th>
                                    <th className="px-2 py-2 text-right">Routes electrified</th>
                                    <th className="px-2 py-2 text-right">Blocks simulated</th>
                                    <th className="px-2 py-2 text-right">Native blocks</th>
                                    <th className="px-2 py-2 text-right">FIFO backfilled</th>
                                    <th className="px-2 py-2 text-right">FIFO trips</th>
                                    <th className="px-2 py-2 text-right">Blocks electrified</th>
                                    <th className="px-2 py-2 text-right">Feasible</th>
                                    <th className="px-2 py-2 text-right">Week MWh</th>
                                </tr>
                            </thead>
                            <tbody>
                                {agencies.map((a) => (
                                    <tr key={`${a.feed_id}-${a.name}`} className="border-t border-gray-100">
                                        <td className="px-2 py-1.5 font-medium text-gray-800">{a.name}</td>
                                        <td className="px-2 py-1.5 text-gray-500">{a.feed_id}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.route_count)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.routes_electrified)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.n_blocks)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.n_blocks_native)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.n_blocks_fifo_backfilled)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.n_trips_fifo_backfilled)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.blocks_electrified)}</td>
                                        <td className="px-2 py-1.5 text-right">{formatCount(a.n_feasible)}</td>
                                        <td className="px-2 py-1.5 text-right">
                                            {a.week_kwh_electrified != null ? (Number(a.week_kwh_electrified) / 1000).toFixed(1) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!agencies.length && (
                        <p className="mt-2 text-xs text-gray-400">No agency rows in the transit snapshot yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function CaTransitCasePage() {
    const { isLoaded } = useGoogleMaps();
    const [mapInstance, setMapInstance] = useState(null);
    const [caseDetail, setCaseDetail] = useState(null);
    const [hexData, setHexData] = useState([]);
    const [hexDataStats, setHexDataStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedHex, setSelectedHex] = useState(null);
    const [hexProfile, setHexProfile] = useState(null);
    const [loadFilter, setLoadFilter] = useState(LOAD_FILTERS.combined);
    const [modalOpen, setModalOpen] = useState(false);
    const [year, setYear] = useState(CA_HD_YEAR);
    const [pageTab, setPageTab] = useState('map');
    const [mapZoom, setMapZoom] = useState(CA_MAP_ZOOM);
    const mapRef = useRef(null);

    const authHeaders = useCallback(() => {
        const token = localStorage.getItem('authToken');
        return token ? { Authorization: `Token ${token}` } : {};
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function fetchAll() {
            setLoading(true);
            setError(null);
            try {
                const headers = authHeaders();
                const [detailRes, transitHexRes, hdHexRes, hdDetailRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.caTransitCase, { params: { year }, headers }),
                    axios.get(API_ENDPOINTS.caTransitCaseHexData, { params: { year }, headers }),
                    axios.get(API_ENDPOINTS.caStatewiseCaseHexData, {
                        params: { case_name: CA_HD_CASE_NAME, year: CA_HD_YEAR },
                        headers,
                    }).catch(() => ({ data: { hex_data: [] } })),
                    axios.get(API_ENDPOINTS.caStatewiseCase, {
                        params: { case_name: CA_HD_CASE_NAME },
                        headers,
                    }).catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                setCaseDetail(detailRes.data);
                const hdRows = hdHexRes.data?.hex_data || [];
                const transitRows = transitHexRes.data?.hex_data || [];
                const merged = mergeLoadHexes(hdRows, transitRows);
                const hdEnergy = merged.reduce((sum, h) => sum + num(h.hd_annual_energy_kwh), 0);
                const transitEnergy = merged.reduce((sum, h) => sum + num(h.transit_annual_energy_kwh), 0);
                setHexDataStats({
                    hexCount: merged.length,
                    hdHexCount: merged.filter((h) => num(h.hd_annual_energy_kwh) > 0).length,
                    transitHexCount: merged.filter((h) => num(h.transit_annual_energy_kwh) > 0).length,
                    totalMwh: (hdEnergy + transitEnergy) / 1000,
                    hdAnnualKwh: hdEnergy,
                    transitAnnualKwh: transitEnergy,
                    hdVehicleCount: hdDetailRes.data?.input_summary?.target_ev_count
                        ?? detailRes.data?.hd?.vehicle_count
                        ?? null,
                    transitVehicleCount: detailRes.data?.fleet?.n_vehicles
                        ?? detailRes.data?.report?.cec_stock
                        ?? null,
                    peakKw: merged.reduce((max, h) => Math.max(max, num(h.peak_power_kw)), 0),
                });
                setHexData(merged);
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load transit load.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchAll();
        return () => { cancelled = true; };
    }, [authHeaders, year]);

    const selectHex = useCallback(async (hexObj) => {
        const h3_index = hexObj?.h3_index;
        if (!h3_index) return;
        const resolution = hexObj.resolution ?? safeResolution(h3_index);
        const isAggregated = Boolean(hexObj.aggregated) || Boolean(hexObj.child_count) || resolution < NATIVE_HEX_RES;
        setSelectedHex({ ...hexObj, resolution, aggregated: isAggregated });
        setHexProfile({ ...hexObj, loading: !isAggregated, aggregated: isAggregated, resolution });
        setModalOpen(false);
        if (mapInstance) {
            try {
                const [lat, lng] = cellToLatLng(h3_index);
                mapInstance.panTo({ lat, lng });
                if (!isAggregated && mapInstance.getZoom() < HEX_ZOOM_ON_SELECT) mapInstance.setZoom(HEX_ZOOM_ON_SELECT);
            } catch (_) {}
        }
        if (isAggregated) return;
        try {
            const res = await axios.get(API_ENDPOINTS.caTransitCaseHexProfile, {
                params: { h3_index, year },
                headers: authHeaders(),
            });
            setHexProfile({ ...res.data, loading: false, resolution });
        } catch (err) {
            setHexProfile((prev) => ({
                ...prev,
                loading: false,
                error: err?.response?.data?.detail || 'Could not load hex profile.',
            }));
        }
    }, [authHeaders, mapInstance, year]);

    useEffect(() => {
        if (selectedHex && !hexMatchesFilter(selectedHex, loadFilter)) {
            setSelectedHex(null);
            setHexProfile(null);
            setModalOpen(false);
        }
    }, [loadFilter, selectedHex]);

    const fleet = caseDetail?.fleet || {};
    const hd = caseDetail?.hd || {};
    const visibleHexes = useMemo(
        () => hexData
            .filter((hex) => hexMatchesFilter(hex, loadFilter))
            .map((hex) => ({
                ...hex,
                display_energy_kwh: displayEnergyKwh(hex, loadFilter),
            })),
        [hexData, loadFilter]
    );
    const displayRes = resolutionForZoom(mapZoom);
    const displayedHexes = useMemo(
        () => aggregateHexes(visibleHexes, displayRes).map((hex) => ({
            ...hex,
            display_energy_kwh: displayEnergyKwh(hex, loadFilter),
        })),
        [visibleHexes, displayRes, loadFilter]
    );
    const energyScale = useMemo(
        () => buildRelativeEnergyScale(displayedHexes, 'display_energy_kwh'),
        [displayedHexes]
    );
    const displayedSelection = useMemo(
        () => selectionForDisplay(selectedHex, displayedHexes, displayRes),
        [selectedHex, displayedHexes, displayRes]
    );
    const hdVehicleCount = hexDataStats?.hdVehicleCount ?? hd.vehicle_count;
    const report = caseDetail?.report || {};
    const transitVehicleCount = report.cec_stock ?? fleet.n_vehicles ?? hexDataStats?.transitVehicleCount;
    const transitYears = caseDetail?.available_years?.length ? caseDetail.available_years : TRANSIT_YEARS;
    const statewideAnnualKwh = loadFilter === LOAD_FILTERS.hd
        ? num(hexDataStats?.hdAnnualKwh)
        : loadFilter === LOAD_FILTERS.transit
            ? num(hexDataStats?.transitAnnualKwh)
            : num(hexDataStats?.hdAnnualKwh) + num(hexDataStats?.transitAnnualKwh);
    const statewidePeakKw = loadFilter === LOAD_FILTERS.hd
        ? num(hd.peak_kw)
        : loadFilter === LOAD_FILTERS.transit
            ? num(caseDetail?.load?.peak_kw)
            : null;
    const statewidePeakLabel = loadFilter === LOAD_FILTERS.transit
        ? (caseDetail?.load?.peak_hour_label || null)
        : null;
    const loadTableRows = [
        {
            id: LOAD_FILTERS.combined,
            label: 'Combined',
            energy: num(hexDataStats?.hdAnnualKwh) + num(hexDataStats?.transitAnnualKwh),
            hexes: hexDataStats?.hexCount,
        },
        {
            id: LOAD_FILTERS.hd,
            label: 'Heavy Duty',
            energy: num(hexDataStats?.hdAnnualKwh),
            hexes: hexDataStats?.hdHexCount,
        },
        {
            id: LOAD_FILTERS.transit,
            label: 'Transit',
            energy: num(hexDataStats?.transitAnnualKwh),
            hexes: hexDataStats?.transitHexCount,
        },
    ];
    const activeAnalysis = analysisForFilter(hexProfile, loadFilter);
    const hasFullProfile = Array.isArray(activeAnalysis?.hourly_profile_8760_kwh)
        && activeAnalysis.hourly_profile_8760_kwh.length > 0;
    const sidebarDaily = Array.isArray(activeAnalysis?.daily_avg_profile_kwh)
        ? activeAnalysis.daily_avg_profile_kwh
        : [];
    const sidebarSeasonal = activeAnalysis?.seasonal_profiles || {};
    const handleMapLoad = useCallback((map) => {
        mapRef.current = map;
        setMapInstance(map);
        setMapZoom(map.getZoom() ?? CA_MAP_ZOOM);
    }, []);
    const handleZoomChanged = useCallback(() => {
        const z = mapRef.current?.getZoom();
        if (z != null) setMapZoom(z);
    }, []);

    return (
        <div className="flex flex-col bg-gray-50" style={{ height: '100vh' }}>
            <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2.5">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            GridPlan · Under development
                        </p>
                        <h1 className="text-lg font-bold leading-tight text-gray-900">
                            {caseDetail?.case_name || 'California HD + Transit Load'}
                        </h1>
                        <p className="mt-0.5 text-xs text-gray-500">
                            California · 2027 heavy-duty trucks + {year} CEC transit-bus stock
                        </p>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-2">
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                            development
                        </span>
                        <div className="flex rounded-lg border border-gray-200 p-0.5">
                            {['map', 'report'].map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setPageTab(tab)}
                                    className={`rounded-md px-3 py-1 text-xs font-semibold capitalize ${
                                        pageTab === tab ? 'bg-violet-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Transit year</span>
                    {transitYears.map((y) => (
                        <button
                            key={y}
                            type="button"
                            onClick={() => { setYear(y); setSelectedHex(null); setHexProfile(null); }}
                            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                year === y ? 'bg-violet-600 text-white' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {y}
                        </button>
                    ))}
                </div>
            </div>

            {pageTab === 'report' ? (
                <TransitReportTab report={report} year={year} onBack={() => setPageTab('map')} />
            ) : (
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="relative min-h-0 min-w-0 flex-1">
                    {loading && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80">
                            <div className="flex flex-col items-center gap-3">
                                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-violet-500" />
                                <p className="text-sm font-medium text-gray-500">Loading combined hex data…</p>
                            </div>
                        </div>
                    )}
                    {error && !loading && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center">
                            <div className="max-w-sm rounded-xl border border-red-200 bg-red-50 p-6 text-center shadow">
                                <p className="mb-1 font-semibold text-red-700">Failed to load case</p>
                                <p className="text-sm text-red-500">{error}</p>
                            </div>
                        </div>
                    )}
                    <div className="absolute inset-0">
                        {isLoaded && (
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={CA_MAP_CENTER}
                                zoom={CA_MAP_ZOOM}
                                options={MAP_OPTIONS}
                                onLoad={handleMapLoad}
                                onZoomChanged={handleZoomChanged}
                                onIdle={handleZoomChanged}
                            >
                                {mapInstance && (
                                    <CaHexOverlay
                                        map={mapInstance}
                                        hexData={displayedHexes}
                                        onHexClick={selectHex}
                                        selectedH3Index={displayedSelection?.h3_index}
                                        loadFilter={loadFilter}
                                        displayRes={displayRes}
                                        energyScale={energyScale}
                                    />
                                )}
                            </GoogleMap>
                        )}
                    </div>
                    {!loading && displayedHexes.length > 0 && !selectedHex && (
                        <div className="pointer-events-none absolute bottom-5 right-3 z-10 rounded-lg border border-gray-200 bg-white/90 px-3 py-2 text-xs text-gray-500 shadow">
                            {displayRes < NATIVE_HEX_RES
                                ? 'Zoom in to see finer hexagons, or click one for its charging profile'
                                : 'Click a hexagon to view its charging profile'}
                        </div>
                    )}
                    <div className="pointer-events-none absolute bottom-5 left-3 z-10 rounded-lg border border-gray-200 bg-white p-3 text-xs shadow">
                        <p className="mb-1.5 font-semibold text-gray-700">
                            {loadFilter === LOAD_FILTERS.hd
                                ? 'Heavy-duty annual load'
                                : loadFilter === LOAD_FILTERS.transit
                                    ? 'Transit annual load'
                                    : 'Combined annual load'}
                        </p>
                        {(energyScale.legend || []).map((item) => (
                            <div key={item.label} className="mb-0.5 flex items-center gap-1.5">
                                <div className="h-3 w-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
                                <span className="text-gray-600">{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex min-h-0 flex-shrink-0 flex-col border-l border-gray-200 bg-white" style={{ width: SIDEBAR_WIDTH }}>
                    <div className="flex-shrink-0 space-y-3 border-b border-gray-100 px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                            {[
                                { id: LOAD_FILTERS.combined, label: 'Combined' },
                                { id: LOAD_FILTERS.hd, label: 'Heavy Duty' },
                                { id: LOAD_FILTERS.transit, label: 'Transit' },
                            ].map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setLoadFilter(opt.id)}
                                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                        loadFilter === opt.id
                                            ? 'bg-violet-600 text-white'
                                            : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-semibold text-gray-900">Statewide summary</p>
                            <table className="w-full overflow-hidden rounded-md border border-gray-200 tabular-nums">
                                <tbody>
                                    <tr className="border-b border-gray-100">
                                        <td className="px-2.5 py-2 text-sm text-gray-600">Total Annual Load</td>
                                        <td className="px-2.5 py-2 text-right">
                                            <div className="text-base font-semibold text-gray-900">{formatEnergyKwh(statewideAnnualKwh)}</div>
                                            {statewideAnnualKwh > 0 && (
                                                <div className="text-[11px] font-normal text-gray-400">
                                                    {formatDailyEnergy(statewideAnnualKwh / 365)} / day
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                        <td className="px-2.5 py-2 text-sm text-gray-600">Peak Demand</td>
                                        <td className="px-2.5 py-2 text-right">
                                            <div className="text-base font-semibold text-gray-900">
                                                {statewidePeakKw ? formatKw(statewidePeakKw) : '—'}
                                            </div>
                                            {statewidePeakLabel && (
                                                <div className="text-[11px] font-normal leading-snug text-gray-400">{statewidePeakLabel}</div>
                                            )}
                                            {loadFilter === LOAD_FILTERS.combined && (
                                                <div className="text-[11px] font-normal leading-snug text-gray-400">Not coincident across HD + transit</div>
                                            )}
                                        </td>
                                    </tr>
                                    {loadFilter !== LOAD_FILTERS.transit && (
                                        <tr className="border-b border-gray-100">
                                            <td className="px-2.5 py-2 text-sm text-gray-600">Heavy-duty truck stock</td>
                                            <td className="px-2.5 py-2 text-right">
                                                <div className="text-base font-semibold text-gray-900">{formatCount(hdVehicleCount)}</div>
                                            </td>
                                        </tr>
                                    )}
                                    {loadFilter !== LOAD_FILTERS.hd && (
                                        <tr>
                                            <td className="px-2.5 py-2 text-sm text-gray-600">Transit bus stock</td>
                                            <td className="px-2.5 py-2 text-right">
                                                <div className="text-base font-semibold text-gray-900">{formatCount(transitVehicleCount)}</div>
                                                <div className="text-[11px] font-normal text-gray-400">
                                                    CEC {year} · share {(Number(report.electrify_share) * 100 || 0).toFixed(1)}% of routes
                                                </div>
                                                {report.n_blocks_electrified != null && (
                                                    <div className="text-[11px] font-normal text-gray-400">
                                                        {formatCount(report.n_blocks_electrified)} vehicle-weeks electrified
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <table className="w-full overflow-hidden rounded-md border border-gray-200 text-sm tabular-nums">
                                <thead>
                                    <tr className="border-b border-gray-100 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                        <th className="px-2.5 py-1.5 font-semibold">Source</th>
                                        <th className="px-2.5 py-1.5 font-semibold">Annual</th>
                                        <th className="px-2.5 py-1.5 font-semibold">Hexes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadTableRows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className={`cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                                loadFilter === row.id ? 'bg-violet-50' : 'bg-white hover:bg-gray-50'
                                            }`}
                                            onClick={() => setLoadFilter(row.id)}
                                        >
                                            <td className="px-2.5 py-1.5 font-semibold text-gray-800">{row.label}</td>
                                            <td className="px-2.5 py-1.5 text-gray-900">{formatEnergyKwh(row.energy)}</td>
                                            <td className="px-2.5 py-1.5 text-gray-600">{formatCount(row.hexes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex-shrink-0 border-b border-gray-100 px-4 pb-3 pt-3">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Search Hexagon</p>
                        <HexSearchBar hexData={visibleHexes} onSelect={selectHex} />
                    </div>

                    {selectedHex && hexProfile && (
                        <div className="flex min-h-0 flex-1 flex-col">
                            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                    disabled={!hasFullProfile || hexProfile.loading}
                                    className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                                        hasFullProfile && !hexProfile.loading
                                            ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-sm'
                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    {hexProfile.loading
                                        ? 'Loading full profile…'
                                        : hasFullProfile
                                            ? 'View Full Analysis'
                                            : 'Full profile unavailable'}
                                </button>
                            </div>
                            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Hex summary</p>
                                    <p className="font-mono text-xs text-gray-800">{hexProfile.h3_index}</p>
                                </div>
                                <button type="button" onClick={() => { setSelectedHex(null); setHexProfile(null); setModalOpen(false); }} className="text-xs text-gray-400 hover:text-gray-700">
                                    Close
                                </button>
                            </div>
                            {hexProfile.error && (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600">{hexProfile.error}</div>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                            <th className="py-1.5 pr-2 font-semibold">Load</th>
                                            <th className="py-1.5 pr-2 font-semibold">Annual</th>
                                            <th className="py-1.5 pr-2 font-semibold">Peak</th>
                                            <th className="py-1.5 font-semibold">Daily</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            {
                                                id: 'combined',
                                                label: 'Combined',
                                                energy: hexProfile.annual_energy_kwh,
                                                peak: hexProfile.peak_power_kw,
                                                daily: hexProfile.daily_energy_kwh ?? (num(hexProfile.annual_energy_kwh) / 365),
                                            },
                                            {
                                                id: 'hd',
                                                label: 'Heavy Duty',
                                                energy: hexProfile.hd_annual_energy_kwh,
                                                peak: hexProfile.hd_peak_power_kw,
                                                daily: hexProfile.hd_daily_energy_kwh ?? (num(hexProfile.hd_annual_energy_kwh) / 365),
                                            },
                                            {
                                                id: 'transit',
                                                label: 'Transit',
                                                energy: hexProfile.transit_annual_energy_kwh,
                                                peak: hexProfile.transit_peak_power_kw,
                                                daily: hexProfile.transit_daily_energy_kwh ?? (num(hexProfile.transit_annual_energy_kwh) / 365),
                                            },
                                        ].map((row) => (
                                            <tr
                                                key={row.id}
                                                className={`border-b border-gray-100 ${
                                                    (row.id === 'combined' && loadFilter === LOAD_FILTERS.combined)
                                                    || (row.id === 'hd' && loadFilter === LOAD_FILTERS.hd)
                                                    || (row.id === 'transit' && loadFilter === LOAD_FILTERS.transit)
                                                        ? 'bg-violet-50'
                                                        : ''
                                                }`}
                                            >
                                                <td className="py-2 pr-2 font-semibold text-gray-800">{row.label}</td>
                                                <td className="py-2 pr-2 text-gray-900">{formatKwh(row.energy)}</td>
                                                <td className="py-2 pr-2 text-gray-900">{formatKw(row.peak)}</td>
                                                <td className="py-2 text-gray-900">{formatDailyEnergy(row.daily)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[10px] leading-relaxed text-gray-400">
                                {hexProfile.aggregated
                                    ? 'This cell is a zoomed-out parent. Zoom in to native hexagons for the hourly profile.'
                                    : 'Combined hourly analysis is HD 8760 plus a repeating transit week. Switch the load filter to open full analysis for that source.'}
                            </p>
                            {hexProfile.peak_hour_label && loadFilter !== LOAD_FILTERS.hd && (
                                <p className="text-xs text-gray-500">Transit peak hour {hexProfile.peak_hour_label}</p>
                            )}
                            {sidebarDaily.some((v) => Number(v || 0) > 0) && (
                                <div className="rounded-lg bg-gray-50 p-3">
                                    <DailyProfileChart
                                        values={sidebarDaily}
                                        color={loadFilter === LOAD_FILTERS.transit ? '#7c3aed' : loadFilter === LOAD_FILTERS.hd ? '#f97316' : '#2563eb'}
                                        title={`${loadFilter === LOAD_FILTERS.hd ? 'Heavy duty' : loadFilter === LOAD_FILTERS.transit ? 'Transit' : 'Combined'} avg daily profile`}
                                    />
                                </div>
                            )}
                            {['spring', 'summer', 'fall', 'winter'].some((s) => Array.isArray(sidebarSeasonal[s]) && sidebarSeasonal[s].some((v) => Number(v || 0) > 0)) && (
                                <div>
                                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Seasonal profiles</p>
                                    <div className="space-y-3">
                                        {['spring', 'summer', 'fall', 'winter'].map((s) => {
                                            const vals = sidebarSeasonal[s];
                                            if (!vals || !vals.some((v) => Number(v || 0) > 0)) return null;
                                            return (
                                                <div key={s} className="rounded-lg bg-gray-50 p-3">
                                                    <DailyProfileChart
                                                        values={vals}
                                                        color={SEASON_COLORS[s]}
                                                        title={s.charAt(0).toUpperCase() + s.slice(1)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {loadFilter !== LOAD_FILTERS.hd && Array.isArray(hexProfile.hour_of_day_kw) && hexProfile.hour_of_day_kw.some((v) => Number(v || 0) > 0) && (
                                <HourSeriesChart values={hexProfile.hour_of_day_kw} title="Transit mean hour-of-day (kW)" color="#7c3aed" />
                            )}
                            {loadFilter !== LOAD_FILTERS.hd && Array.isArray(hexProfile.hourly_kw) && hexProfile.hourly_kw.some((v) => Number(v || 0) > 0) && (
                                <HourSeriesChart values={hexProfile.hourly_kw} title="Transit representative week (kW)" color="#2563eb" />
                            )}
                            </div>
                        </div>
                    )}

                    {!selectedHex && (
                        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
                            <div>
                                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Simulation</p>
                                <div className="space-y-2">
                                    {[
                                        ['CEC bus stock', formatCount(report.cec_stock)],
                                        ['GTFS routes', formatCount(report.total_routes)],
                                        ['Electrify share', report.electrify_share != null ? `${(report.electrify_share * 100).toFixed(1)}%` : '—'],
                                        ['Simulated vehicle-weeks', formatCount(report.n_blocks_simulated ?? fleet.n_blocks)],
                                        ['Electrified vehicle-weeks', formatCount(report.n_blocks_electrified)],
                                        ['FIFO backfilled blocks', formatCount(report.n_blocks_fifo_backfilled)],
                                        ['Agencies', formatCount(report.n_agencies ?? fleet.n_agencies)],
                                        ['Week energy', formatKwh(fleet.total_week_kwh)],
                                        ['Charging', caseDetail?.assumptions?.mode || 'passive'],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex items-baseline justify-between gap-2">
                                            <span className="text-xs text-gray-400">{k}</span>
                                            <span className="text-right text-xs font-medium text-gray-700">{v ?? '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {fleet.depot_note && (
                                <p className="text-xs leading-relaxed text-gray-500">{fleet.depot_note}</p>
                            )}
                            <p className="pt-1 text-xs italic text-gray-400">
                                Hex colors show every cell with HD and/or transit load, using the active filter. Combined is the sum, not the intersection.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            )}
            <HexAnalyticsModal
                key={`${selectedHex?.h3_index || ''}-${loadFilter}`}
                open={modalOpen && !!activeAnalysis}
                onClose={() => setModalOpen(false)}
                h3_index={hexProfile?.h3_index || selectedHex?.h3_index}
                profile={activeAnalysis}
                subtitle={analysisSubtitle(loadFilter)}
            />
        </div>
    );
}
